import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import closeModal from 'Helpers/ModalHelper';
import useCollection from 'Hooks/useCollection';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import useMobile from 'Hooks/useMobileLayout';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { ClientActionItemOverride } from 'Overrides/ActionItem';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { dispatchActionItemRefresh } from 'Hooks/useRefreshEvent';
import { addQueryWithReplace } from 'Helpers/Query';
import { FDDialog, FDDialogContent } from '@facet-wealth/design';
import MetaCollectionFormInline from 'Components/UI/Meta/MetaForm/MetaCollectionFormInline';

function ClientActionItemModal({ actionItemId }) {
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();
  const { collection, setCollection } = useCollection();
  const { householdResource } = useSimpleHousehold();
  const [loading, setLoading] = useState(false);
  const isMobile = useMobile();

  const handleClose = () => {
    closeModal();
  };

  useEffect(() => {
    // Only fetch if you haven't before and you have the required relationship
    if (
      !collection
      && householdResource
      && householdResource.relationship('actionItems')
      && householdResource.relationship('actionItems').link('related')
    ) {
      setLoading(true);
      householdResource
        .relationship('actionItems')
        .link('related')
        .Get({
          metaOverrideSet: ClientActionItemOverride, // Override resource returned by the get
          params: actionItemId ? { id: actionItemId } : { limit: 0, offset: 0 },
        })
        .then((coll) => {
          if (coll.resources().length() === 0) {
            coll.create({
              metaOverrideSet: ClientActionItemOverride, // Override resources that are created on the collection
              addToCollection: true,
            });
          }
          setCollection(coll);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          displayErrorMessage(err, err.errors);
        });
    }
  }, [householdResource]);

  const onSuccess = () => {
    const refreshKey = actionItemId ? 'action-items' : 'add-action-item';
    addQueryWithReplace({ refresh: refreshKey });
    displaySuccess('Task saved');
    dispatchActionItemRefresh();
    handleClose();
  };

  const onFailure = (err) => {
    displayErrorMessage(err, err.errors);
  };

  if (loading || !collection) {
    return <LoadingOverlay />;
  }

  const mobileLayout = [['name'], ['dueDate'], ['priority']];
  const desktopLayout = [['name'], ['dueDate', 'priority']];

  return (
    <FDDialog
      open
      fullScreen={isMobile}
      onClose={handleClose}
      title={actionItemId ? 'Action Item' : 'Add Action Item'}
      maxWidth="lg"
    >
      <FDDialogContent>
        <MetaCollectionFormInline
          collection={collection}
          FormProps={{
            onSubmit: onSuccess,
            onFailure,
          }}
          rows={isMobile ? mobileLayout : desktopLayout}
          title={actionItemId ? 'Action Item' : 'Add Action Item'}
        />
      </FDDialogContent>
    </FDDialog>
  );
}

ClientActionItemModal.defaultProps = {
  actionItemId: null,
};

ClientActionItemModal.propTypes = {
  actionItemId: PropTypes.string,
};

export default ClientActionItemModal;
